import { Clear, Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormHelperText,
  IconButton,
  InputAdornment,
  Popper,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { SearchResultItem } from '@retail/components';
import { useDebounce } from '@retail/hooks';
import { useMgSupplierItemsSearch } from '@retail/products/data-access';
import { useProductsTFunction } from '@retail/products/i18n';
import { useKeypress } from '@shared/hooks';
import { getOS } from '@shared/utils';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { MgSupplierItemSearchResultDTO } from '@retail/products/types';

type SearchStatus = 'noSearch' | 'searching' | 'error' | 'noResult' | 'success';

interface Props {
  onItemClick: (item: MgSupplierItemSearchResultDTO) => void;
  sx?: SystemStyleObject<Theme>;
}

const currentOs = getOS();

const focusHotkeyText = currentOs === 'mac' ? '⌘ K' : 'ctrl K';

export function MgItemSearchAutocomplete({ onItemClick, sx = {} }: Props) {
  const t = useProductsTFunction();
  const { appTenant } = useSelectedOrgUnit();
  const { register, watch, setValue, setFocus } = useForm<{ search: string }>({
    mode: 'onChange',
    defaultValues: { search: '' },
  });
  const inputValue = watch('search');
  const query = useDebounce(inputValue, 250);

  const { data, isLoading, error } = useMgSupplierItemsSearch(
    { page: 0, size: 10, query },
    { enabled: !!query, suspense: false, retry: 0 }
  );

  const onClick: Props['onItemClick'] = useCallback(
    (item) => {
      onItemClick(item);
      setValue('search', '');
    },
    [onItemClick, setValue]
  );

  const results = data?.result || [];
  const searchField = register('search');

  const searchStatus: SearchStatus = useMemo(() => {
    if (error) {
      return 'error';
    }
    if (isLoading) {
      return 'searching';
    }
    if (!query) {
      return 'noSearch';
    }
    return results?.length ? 'success' : 'noResult';
  }, [query, error, isLoading, results?.length]);

  const PopperComponent = useMemo(() => {
    switch (searchStatus) {
      case 'searching':
        return results?.length ? Popper : NullComponent;
      case 'error':
      case 'noSearch':
        return NullComponent;
      case 'noResult':
      case 'success':
      default:
        return Popper;
    }
  }, [results?.length, searchStatus]);

  const setInputFocusCallback = useCallback(
    (event: KeyboardEvent) => {
      if (
        ((currentOs === 'mac' && event.metaKey) || (currentOs !== 'mac' && event.ctrlKey)) &&
        event.code === 'KeyK'
      ) {
        setFocus('search');
      }
    },
    [setFocus]
  );

  useKeypress(setInputFocusCallback);

  return (
    <Autocomplete
      inputValue={inputValue}
      sx={(theme) => ({ ...autoCompleteSx(theme), ...sx })}
      renderInput={(params) => {
        // @ts-ignore
        const isFocused = params.inputProps?.ref?.current === document.activeElement;

        return (
          <>
            <TextField
              {...params}
              {...searchField}
              slotProps={{
                input: {
                  ...params.InputProps,
                  sx: {
                    paddingRight: '9px !important',
                  },
                  endAdornment: inputValue ? (
                    <InputAdornment position="end">
                      {isLoading ? (
                        <CircularProgress color="inherit" size={24} />
                      ) : (
                        <Tooltip title={t('products.mgItem.search.reset')}>
                          <IconButton onClick={() => setValue('search', '')}>
                            <Clear />
                          </IconButton>
                        </Tooltip>
                      )}
                    </InputAdornment>
                  ) : (
                    !isFocused && (
                      <InputAdornment position="end">
                        <Box
                          border="1px solid"
                          borderColor="divider"
                          fontSize="0.85rem"
                          fontWeight={600}
                          paddingX="6px"
                          borderRadius="7px"
                          lineHeight="28px"
                        >
                          {focusHotkeyText}
                        </Box>
                      </InputAdornment>
                    )
                  ),
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="search-icon" />
                    </InputAdornment>
                  ),
                },
              }}
              error={!!error}
              placeholder={t('products.mgItem.search.label')}
              fullWidth
            />
            {!!error && <FormHelperText error>{t('products.errors.generic')}</FormHelperText>}
          </>
        );
      }}
      options={results}
      renderOption={(props, option) => {
        const { key, ...muiProps } = props;
        return (
          <SearchResultItem
            key={`${key}${option.mgSupplierItemId}`}
            liProps={{ ...muiProps, onClick: () => onClick(option) }}
            externalItemSource={appTenant.externalItemSource}
            includeSupplierInfo
            {...option}
          />
        );
      }}
      clearOnBlur={false}
      filterOptions={(option) => option} // must be set, see https://mui.com/material-ui/react-autocomplete/#search-as-you-type
      getOptionLabel={({ primaryText, mgItemNumber }) => primaryText || `${mgItemNumber}`}
      slots={{ popper: PopperComponent }}
      slotProps={{
        popper: { style: { width: 'fit-content' }, sx: { ...popperSx } },
        listbox: {
          sx: {
            maxHeight: '25rem',
            '& li': {
              borderTop: '1px solid',
              borderColor: 'divider',
              '&:last-child': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
            },
            ...listboxSx,
          },
        },
      }}
      noOptionsText={t('products.mgItem.search.noResults')}
    />
  );
}

function NullComponent() {
  return null;
}

const autoCompleteSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '&.Mui-focused .search-icon': {
    color: 'primary.main',
  },
  '& .Mui-error .search-icon': {
    color: 'error.main',
  },
  '& .MuiInputBase-root.MuiAutocomplete-inputRoot': {
    paddingY: '4px',
  },
  ...theme.applyStyles('dark', {
    '&.Mui-focused .search-icon': { color: 'primary.light' },
  }),
});

const popperSx: SystemStyleObject<Theme> = {
  '& .MuiPaper-root': {
    p: 0,
    boxShadow: (theme) => theme.shadows[9],
  },
};

const listboxSx: SystemStyleObject<Theme> = {
  '& .MuiAutocomplete-option': {
    paddingX: '24px !important',
    paddingY: '12px !important',
    borderRadius: 0,
  },
};
